import type { Client, Conversation, Message } from "@twilio/conversations";
import { useEffect, useRef } from "react";
import { useUnreadStore } from "../store/useOrderStore";
import { useChatNotificationSound } from "./useChatNotificationSound";

type BusOpts = {
  client: Client | null;
  identity: string | null; 
  selfUserId: string;
  openChatUniqueName?: string | null;
};

export function useConversationsManager(opts: BusOpts) {
  const { client, identity, selfUserId, openChatUniqueName } = opts;
  const openChatUniqueNameRef = useRef<string | null>(null);
  const initializedRef = useRef(false);
  const playNotificationSound = useChatNotificationSound();

  async function safeGetUnreadCount(conv: any): Promise<number> {
    try {
      const count: number | null = await conv.getUnreadMessagesCount();
      if (count !== null) return count;
      return conv.getMessagesCount();
    } catch (error: any) {
      console.warn(
        `Failed to get unread count for conversation ${conv.uniqueName}:`,
        error
      );
      return 0;
    }
  }

  useEffect(() => {
    openChatUniqueNameRef.current = openChatUniqueName ?? null;
  }, [openChatUniqueName]);

  const attach = async (conv: Conversation) => {
    try {
      const initialUnreadCount = await safeGetUnreadCount(conv);
      if (typeof initialUnreadCount === "number" && conv.uniqueName) {
        useUnreadStore
          .getState()
          .setUnreadCountByUniqueName(
            conv.uniqueName,
            Math.max(0, initialUnreadCount ?? 0)
          );
      }
    } catch (err) {
      console.error(
        `Failed to get initial unread count for ${conv.uniqueName}:`,
        err
      );
    }
  };

  const initializeConversations = async () => {
    if (!client || initializedRef.current) {
      return { conversations: [], cleanup: () => {} };
    }
    initializedRef.current = true;

    try {
      const conversations = await client.getSubscribedConversations();
      for (const conversation of conversations.items) {
        await attach(conversation);
      }

      const onConversationAdded = async (conv: Conversation) => {
        await attach(conv);
        useUnreadStore.getState().addConversation(conv);

        try {
          const messageCount = await conv.getMessagesCount();
          if (messageCount > 0) {
            const attributes = conv.attributes || {};
            const chatType = (attributes as any).chatType;
            playNotificationSound(chatType);
          }
        } catch (error) {
          console.warn("Failed to check message count of conversation", error);
        }
      };

      const onConversationLeft = async (conv: Conversation) => {
        useUnreadStore.getState().removeConversation(conv);
      };

      const onMessageAdded = async (message: Message) => {
        if (!identity) return;

        const conversation = message.conversation;
        const uniqueName = conversation.uniqueName;

        if (!uniqueName) return;

        const isMine = message.author === identity;
        const isOpen = openChatUniqueNameRef.current === uniqueName;

        if (!isMine) {
          if (!isOpen) {
            const currentCount =
              useUnreadStore.getState().counts.get(uniqueName) || 0;
            useUnreadStore
              .getState()
              .setUnreadCountByUniqueName(uniqueName, currentCount + 1);

            const attributes = conversation.attributes || {};
            const chatType = (attributes as any).chatType;
            playNotificationSound(chatType);
          } else {
            conversation.setAllMessagesRead().catch(() => {});
          }
        }
      };

      client.on("conversationAdded", onConversationAdded);
      client.on("conversationLeft", onConversationLeft);
      client.on("messageAdded", onMessageAdded);

      return {
        conversations: conversations.items,
        cleanup: () => {
          client.removeListener("conversationAdded", onConversationAdded);
          client.removeListener("conversationLeft", onConversationLeft);
          client.removeListener("messageAdded", onMessageAdded);
          initializedRef.current = false;
        },
      };
    } catch (error) {
      initializedRef.current = false;
      return { conversations: [], cleanup: () => {} };
    }
  };

  return {
    initializeConversations,
  };
}
